import React from 'react';
import { useForm as useRHFForm, } from 'react-hook-form';
import { get, pick, set } from 'lodash-es';
import { HOOK_MARK } from '../constants';
import { getFieldId, getNameListFromFields, merge } from '../utils';
export class FormStore {
    constructor(rhfFrm) {
        this.callbacks = {};
        this.getInitialValues = () => this.rhfForm.control._options.defaultValues;
        this.getFieldValue = (name) => this.rhfForm.getValues(name);
        this.getFieldsValue = (nameList) => {
            if (nameList && nameList.length > 0) {
                const arrayValue = this.rhfForm.getValues(nameList);
                const values = {};
                nameList.forEach((item, index) => {
                    set(values, item, arrayValue[index]);
                });
                return values;
            }
            return this.rhfForm.getValues();
        };
        this.getFieldError = (name) => {
            const { errors: rhfErrors, isValid } = this.rhfForm.formState;
            if (isValid) {
                return [];
            }
            const error = get(rhfErrors, name);
            if (error?.root) {
                return [error.root.message];
            }
            if (Array.isArray(error)) {
                const errors = [];
                error.forEach((item) => {
                    if (item) {
                        Object.keys(item).forEach((key) => {
                            errors.push(item[key].message);
                        });
                    }
                });
                return errors;
            }
            return error?.message ? [error.message] : [];
        };
        this.getFieldsError = (nameList) => {
            // When the field's name is similar to `a.b.c`, using `Object.keys(this.rhfForm.formState.errors)` only get the error information of `a`. It's necessary to iterate through the fields to obtain the complete name.
            const _nameList = nameList ?? getNameListFromFields(this.rhfForm.control._fields);
            const errors = [];
            _nameList.forEach((name) => {
                const rhfFormError = get(this.rhfForm.control._formState.errors, name);
                if (rhfFormError) {
                    if (rhfFormError?.root) {
                        errors.push({ name, errors: [rhfFormError.root.message] });
                    }
                    else if (Array.isArray(rhfFormError)) {
                        rhfFormError.filter(Boolean).forEach((item, index) => {
                            Object.keys(item).forEach((key) => {
                                const deepName = [name, index, key].join('.');
                                const deepErrors = item[key].message;
                                errors.push({ name: deepName, errors: [deepErrors] });
                            });
                        });
                    }
                    else {
                        errors.push({
                            name,
                            errors: [rhfFormError.message],
                        });
                    }
                }
            });
            return errors;
        };
        this.setFieldValue = (name, value) => {
            this.rhfForm.setValue(name, value);
        };
        this.setFieldsValue = (values) => {
            const oldValues = this.rhfForm.getValues();
            this.rhfForm.reset(merge(oldValues, values), {
                keepDefaultValues: true,
            });
        };
        this.resetFields = (nameList) => {
            const oldValues = {
                ...this.getInitialValues(),
            };
            if (!nameList) {
                this.rhfForm.reset(oldValues, {
                    keepDirtyValues: false,
                    keepValues: false,
                    keepDefaultValues: true,
                });
                return;
            }
            const newValues = { ...this.rhfForm.getValues() };
            nameList?.forEach((name) => {
                set(newValues, name, get(oldValues, name));
            });
            this.rhfForm.reset(newValues, {
                keepDirtyValues: false,
                keepValues: false,
                keepDefaultValues: true,
            });
        };
        this.resetField = (name) => {
            this.rhfForm.resetField(name, {
                keepDirty: false,
                keepError: false,
                keepTouched: false,
            });
        };
        this.validateFields = async (nameList) => {
            const isValid = await this.rhfForm.trigger(nameList);
            if (isValid) {
                return this.rhfForm?.getValues();
            }
            return await Promise.reject(this.getFieldsError(nameList));
        };
        this.isValid = () => this.rhfForm.formState.isValid;
        this.isValidating = () => this.rhfForm.formState.isValidating;
        this.setCallbacks = (callbacks) => {
            this.callbacks = callbacks;
        };
        this.submit = async () => {
            const { onFinish, onFinishFailed } = this.callbacks;
            const isValid = await this.rhfForm.trigger();
            if (isValid) {
                try {
                    onFinish?.(this.getFieldsValue());
                }
                catch (err) {
                    // Should print error if user `onFinish` callback failed
                    // eslint-disable-next-line no-console
                    console.error(err);
                }
            }
            else {
                onFinishFailed?.(this.getFieldsError());
            }
        };
        this.watch = (names, callback) => {
            if (typeof names === 'string') {
                return this.rhfForm.watch((values, { name, type }) => {
                    // type in array fields is undefined
                    if (names === name && (type === 'change' || !type)) {
                        callback?.(get(values, names));
                    }
                });
            }
            return this.rhfForm.watch((values, { name, type }) => {
                // type in array fields is undefined
                if (name && names.includes(name) && (type === 'change' || !type)) {
                    const pickedValues = pick(values, names);
                    callback?.(pickedValues);
                }
            });
        };
        this.getInternalHooks = (key) => {
            if (key !== HOOK_MARK) {
                return null;
            }
            return {
                setCallbacks: this.setCallbacks,
                getInitialValues: this.getInitialValues,
                handleSubmit: this.rhfForm.handleSubmit,
                rhfForm: this.rhfForm,
                watch: this.rhfForm.watch,
            };
        };
        this.getExtendsForm = () => ({
            getFieldValue: this.getFieldValue,
            getFieldsValue: this.getFieldsValue,
            getFieldError: this.getFieldError,
            getFieldsError: this.getFieldsError,
            resetFields: this.resetFields,
            resetField: this.resetField,
            setFieldValue: this.setFieldValue,
            setFieldsValue: this.setFieldsValue,
            validateFields: this.validateFields,
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            submit: this.submit,
            isValid: this.isValid,
            isValidating: this.isValidating,
            _getInternalHooks: this.getInternalHooks,
            getInternalRhfForm: () => this.rhfForm,
            watch: this.watch,
        });
        this.rhfForm = rhfFrm;
    }
}
export default function useForm(options) {
    const formRef = React.useRef();
    const rhfForm = useRHFForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        criteriaMode: 'all',
        ...options,
        defaultValues: options?.initialValues,
    });
    if (!formRef.current) {
        const formStore = new FormStore(rhfForm);
        formRef.current = {
            ...formStore.getExtendsForm(),
            scrollToField(name, scrollOptions) {
                const fieldId = getFieldId(name, formRef.current?.__INTERNAL__.name);
                const node = fieldId ? document.getElementById(fieldId) : null;
                if (node) {
                    node.scrollIntoView(scrollOptions);
                }
            },
            // __INTERNAL__ is an object used in form internal, save some static variable
            // Currently only saving the name prop of the form component for `form.scrollToField`
            __INTERNAL__: {},
        };
    }
    return [formRef.current];
}
